/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { CaronteSession, Context, init } from '@cyberdefense-im2/carontesdkweb';
import { from, Observable, throwError } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import {
  CharonService,
  ENTRYPOINT,
  FullHttpOptions,
  HateoasService,
  HTTPOPTIONS,
  IEntrypointResponse,
  IHateoas,
  IHttpOptions,
  IInitializeParam,
  ResultObservable,
} from '../../core';
import { mapCharonParams } from '../../core/handle-params';
import { CHARON_PARAMS, IS_CHARON_REQUEST } from './charon.http.interceptor';
import { CharonSessionService } from './charon.http.session.service';
import { SessionStorageService } from './session-storage.service';

/**
 * CharonService tem como objetivo facilitar a comunicação das aplicações frontend
 * com as APIs expostas pelo Caronte no padrão `Hateoas`.
 *
 * A interface base é a mesma do `HttpClient` do Angular, então toda a estratégia para realização
 * de requisições usada com ele deve funcionar também neste serviço.
 *
 * Sendo assim o [guia do HttpClient original](https://angular.io/guide/http)
 * é fortemente recomendado no contexto de utilização deste serviço.
 *
 *  * **Module:**
 *
 */
@Injectable({
  providedIn: 'root',
})
export class CharonHttpService extends CharonService {
  initialized = false;
  hasCryptography = false;
  private isRefreshingSession = false;
  private relation = '';
  private initializeParam: IInitializeParam;
  private useHref = false;

  /**
   * @internal
   */
  constructor (
    protected hateoas: HateoasService,
    private http: HttpClient,
    protected charonSession: CharonSessionService,
    protected sessionStorageService: SessionStorageService,
    @Optional() @Inject(HTTPOPTIONS) private httpOptions: IHttpOptions | undefined,
    @Optional() @Inject(ENTRYPOINT) private entryPoint: string | undefined,
  ) {
    super(hateoas, charonSession);
  }

  /**
   * Método para adicionar configurações default em memória no HateoasService.
   *
   * Method that adds default configuration in memory ate HateoasService.
   *
   * @param options Recebe objeto do tipo IHateoas, com definições de entrypoint e HttpOptions, ambos opcionais
   */
  config(options: IHateoas): void {
    if (this.initialized) {
      this.hateoas.setConfig(options);
      return;
    }
    this.entryPoint = options.entryPoint || this.entryPoint;
    this.httpOptions = {
      ...this.httpOptions,
      ...options.httpOptions,
    };
  }

  /**
   * Método para inicialização do Caronte na aplicação/micro-frontend garantindo o fluxo de requisão no padrão Hateoas.
   *
   * Method for Charon initialization in the application/micro-frontend and ensure the right request flow.
   *
   * @param parameters Define as propriedades necessárias da interface IInitializeParam para inicialização do Caronte
   */
  initialize<ResultData extends HttpResponse<any>, HttpOptions extends FullHttpOptions>(
    parameters?: IInitializeParam
  ): ResultObservable<ResultData, HttpOptions> {
    if (!this.isRefreshingSession) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.sessionStorageService.removeItem(parameters?.charonID!);
    }
    const localConfig = parameters?.entryPoint || this.entryPoint;
    const body = parameters?.body;
    const baseMethod = parameters?.baseMethod ? parameters.baseMethod : 'GET';
    this.hateoas = this.getHateoasRef(localConfig);
    this.initialized = true;
    const options = parameters?.options ? parameters.options : {};
    options.charonID = parameters?.charonID;
    this.useHref = parameters?.useHref || false;
    this.hateoas.setUseHrefs(this.useHref);

    if (parameters?.headers) {
      const values = this.getEntryPointResponse(parameters.charonID!) !== null;
      options!.headers = values ? this.getEntryPointResponse(parameters.charonID!).headers : parameters.headers;
    }

    if (this.hasCryptography) {
      if (!parameters?.charonID) {
        throw new Error('O parâmetro charonID não foi identificado');
      }
      return this.initSession(parameters, localConfig as string).pipe(
        tap((res) => this.saveEntrypointData(parameters, res))
      );
    }
    return this.request(baseMethod, localConfig, { ...options, ...options.headers, body }).pipe(
      tap((res) => this.saveEntrypointData(parameters!, res as HttpResponse<unknown>)) as any
    );
  }

  /**
   * Método para criar um request http baseado no entrypoint ou relation
   * inputados seguindo o padrão de comunicação Hateoas.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#request
   *
   * Method to create a http request based on the entrypoint or relation inputed.
   *
   * @internal
   * @param first Tipo do método HTTP
   * @param url URL de requisição
   * @param options Configurações a serem enviadas para a requisição.
   * @returns an observable with the http request created by hateoas service.
   */
  request<ResultData, HttpOptions extends FullHttpOptions>(
    first: string | HttpRequest<any>,
    url?: string,
    options: FullHttpOptions = {},
  ): ResultObservable<ResultData, HttpOptions> {

    let req: HttpRequest<any>;

    // ATENÇÃO:
    // O código abaixo é idêntico ao do HttpClient original do Angular, incluindo comentários:

    // First, check whether the primary argument is an instance of `HttpRequest`.
    if (first instanceof HttpRequest) {
      // It is. The other arguments must be undefined (per the signatures) and can be
      // ignored.
      req = first;
    } else {
      // It's a string, so it represents a URL. Construct a request based on it,
      // and incorporate the remaining arguments (assuming `GET` unless a method is
      // provided.

      // Figure out the headers.
      let headers: HttpHeaders | undefined;
      const charonID = options.charonID as string;

      const headersToObject = (headers: HttpHeaders | Record<string, unknown> | undefined) => {
        if (!(headers)) {
          return {};
        }
        if (headers instanceof HttpHeaders) {
          const obj: { [name: string]: string | string[]; } = {};
          headers.keys().forEach(key => obj[key] = headers.getAll(key)!);
          return obj;
        }
        return headers;
      };

      let moduleHeaders = {};
      if (!(this.entryPoint) || this?.entryPoint.includes(charonID)) {
        moduleHeaders = headersToObject(this.httpOptions?.headers as HttpHeaders);
      }
      const sessionHeaders = JSON.parse(this.sessionStorageService.getItem(charonID) as string)?.headers || {};
      const optionHeaders = headersToObject(options.headers as HttpHeaders);

      if (options.headers) {
        headers = new HttpHeaders({
          ...moduleHeaders,
          ...sessionHeaders,
          ...optionHeaders
        });
      } else {
        headers = new HttpHeaders({
          ...moduleHeaders,
          ...sessionHeaders,
        });
      }

      // Sort out parameters.
      let params: unknown | undefined | HttpParams;

      if (options.params instanceof HttpParams) {
        const allParameters = this.addAdditionalParams(options.params) as HttpParams;
        params = this.hasCryptography ?
          allParameters : mapCharonParams(allParameters['updates']);
      } else {
        params = this.hasCryptography ?
          this.addAdditionalParams(new HttpParams({ fromObject: options.params })) :
          this.addAdditionalParams(options.params);
      }

      const httpContext = new HttpContext();
      let context = httpContext.set(IS_CHARON_REQUEST, { charonId: charonID, isEncrypted: this.hasCryptography });


      if (!this.hasCryptography) {
        const parameters = JSON.stringify(params);
        const btoaParameters = btoa(parameters);
        if (headers instanceof HttpHeaders) {

          if (!headers.has('x-charon-params')) {
            headers = headers.append('x-charon-params', btoaParameters);
          }
        } else {
          headers = new HttpHeaders(headers);
          headers = headers.append('x-charon-params', btoaParameters);
        }
      } else {
        context = httpContext.set(CHARON_PARAMS, params);
      }
      // Construct the request.
      // tslint:disable-next-line:no-non-null-assertion
      req = new HttpRequest(first, url!, (options.body !== undefined ? options.body : null), {
        headers,
        context,
        reportProgress: options.reportProgress,
        // By default, JSON is assumed to be returned for all calls.
        responseType: this.hasCryptography ? 'text' : options.responseType || 'json',
        withCredentials: options.withCredentials,
      });
    }

    let header = req.headers;
    const charonID = options.charonID as string;
    const entrypointResponse = JSON.parse(this.sessionStorageService.getItem(charonID)!);
    const session = entrypointResponse?.sessionHeader as string;
    if (session && !req.headers.get('x-charon-session')) {
      header = req.headers.append('x-charon-session', session);
    }

    if (this.isRefreshingSession) {
      this.isRefreshingSession = false;
      if (!this.hasCryptography) {
        header = req.headers.delete('x-charon-session');
      }
    }

    return this.http
      .request(
        req.clone({
          url: this.hateoas.getURL(req.url, options.charonID as string, entrypointResponse),
          headers: header
        }),
      )
      .pipe(
        filter((event: HttpEvent<any>) => event instanceof HttpResponse),
        map(res => {
          this.handleCharonResponse(res as HttpResponse<any>);
          return res;
        }),
        shareReplay(1),
        catchError((error: HttpErrorResponse) => {
          return this.createNewRequest(error, req, entrypointResponse);
        }),
      );
  }

  /**
   * Método base para construção de uma requisição HTTP `GET`.
   *
   * A utilização deste método é a mesma do `HttpClient.get` do Angular com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#get
   *
   * Base method to construct a `GET` request.
   *
   * @param url URL da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  get<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    this.relation = url;
    return this.request<ResultData, HttpOptions>('GET', url, options as any);
  };

  /**
   * Método para construção de uma requisição HTTP `POST`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#post
   *
   * Method to construct a `POST` request.
   *
   * @param url URL da requisição.
   * @param body Dados a serem enviados no corpo da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  post<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    body: any | null,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    this.relation = url;
    return this.request<ResultData, HttpOptions>('POST', url, { ...options, body });
  }

  /**
   * Método para construção de uma requisição HTTP `PUT`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#put
   *
   * Method to construct a `PUT` request.
   *
   * @param url URL da requisição.
   * @param body Dados a serem enviados no corpo da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  put<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    body: any | null,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    this.relation = url;
    return this.request<ResultData, HttpOptions>('PUT', url, { ...options, body });
  }

  /**
   * Método para construção de uma requisição HTTP `DELETE`.
   *
   * A utilização deste método segue o mesmo padrão do `HttpClient.request` do Angular, com todas as possíveis variações
   * de parâmetros do método original. Para mais detalhes, acesse a documentação:
   * https://angular.io/api/common/http/HttpClient#delete
   *
   * Method to construct a `DELETE` request.
   *
   * @param url URL da requisição.
   * @param options Configurações necessárias para envio da requisição.
   *
   * @return Um `Observable<any>` da resposta para a requisição HTTP criada.
   */
  delete<ResultData = any, HttpOptions extends IHttpOptions = {}>(
    url: string,
    options?: HttpOptions,
  ): ResultObservable<ResultData, HttpOptions> {
    this.relation = url;
    return this.request<ResultData, HttpOptions>('DELETE', url, options as any);
  }

  /**
   * Método para tratar a resposta do Caronte e persistir seus dados de sessão em memória.
   *
   * Method to Handle Charon response and persist charonSession on memory.
   *
   * @internal
   * @param event Recebe o evento obtido como resposta do API
   */
  private handleCharonResponse(event: HttpResponse<any>) {
    const { headers, body } = event;
    if (!body) {
      return;
    }
    if (body.links) {
      this.hateoas.setLinks(body.links);
    }
    const charonSessionHeader = headers.get('x-charon-session');
    if (!charonSessionHeader) {
      return;
    }

  }

  /**
   * Método que define se a requisição será ou não criptografada.
   *
   * Method that defines whether or not the request will be encrypted.
   *
   * @param hasCrypto flag que indica se a requisição será criptografada
   */
  setCryptography(hasCrypto: boolean): void {
    this.hasCryptography = hasCrypto;
  }

  /**
   * Método que inicializa a sessão no Caronte
   *
   * Method that init session on Caronte
   *
   * @param charonId ID da API cadastrada no Caronte
   * @param localConfig URL de requisição
   * @param headers headers que devem ser passados em tempo de execução
   */
  private initSession(parameters: IInitializeParam, localConfig: string): Observable<any> {
    const charonBaseUrl = new URL(localConfig);
    const initContext: Context = {
      charonId: parameters.charonID as string,
      charonBaseUrl: charonBaseUrl.origin,
      force: true
    };
    let headers: HttpHeaders;

    if (parameters.headers && parameters.headers instanceof HttpHeaders) {
      headers = parameters.headers;
    } else {
      headers = new HttpHeaders();
    }

    return from(init(initContext))
      .pipe(
        map((caronteSession: CaronteSession) => {
          return { caronteSession };
        }),
        switchMap((res) => {
          headers = headers.append('x-charon-session', res.caronteSession.sessionId as string);
          return this.request('GET', localConfig, { headers, responseType: 'text', charonID: parameters.charonID });
        })
      );
  }

  /**
  * Método que cria uma nova sessão no Caronte
  *
  * Method that create a new session on Caronte
  *
  * @param error Erro recebido da requisição
  * @param request Requisição enviada
  */
  private createNewRequest(error: HttpErrorResponse, request: HttpRequest<any>, entrypointResponse: IEntrypointResponse): Observable<any> {
    let messageError: string;
    if (this.hasCryptography) {

      let message;
      try {
        message = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
        messageError = message.message;
      } catch (e) {
        message = error.message;
        messageError = message;
      }

    } else {
      messageError = error.error?.message;
    }

    const validateError = messageError?.includes('invalid session') || messageError?.includes('InvalidSession');

    if (!validateError) {
      return throwError(() => error);
    }

    this.isRefreshingSession = true;

    return this.initialize(entrypointResponse.initializeParam)
      .pipe(
        filter(res => res != null),
        map((response: any) => {
          this.saveEntrypointData(entrypointResponse.initializeParam, response);
          const sessionHeaders = response.headers.get('x-charon-session');
          return { sessionHeaders, response };
        }),
        switchMap((res: any) => {
          let headers = request.headers;
          headers = request.headers.set('x-charon-session', res.sessionHeaders);
          return this.http.request(request.clone({
            url: this.hateoas.findLink(res.response.body.links, this.relation),
            headers
          }));
        }),
        filter((event: HttpEvent<unknown>) => event instanceof HttpResponse),
      );

  }

  saveEntrypointData(initializeParam: IInitializeParam, event: HttpResponse<any>) {
    if (!event.body) {
      return;
    }
    const sessionHeader = event.headers.get('x-charon-session') as string;

    const hasValues = this.getEntryPointResponse(initializeParam.charonID!) !== null;
    const headerValues = hasValues ? new HttpHeaders(this.getEntryPointResponse(initializeParam.charonID!).headers) : initializeParam.headers;
    const headers = headerValues?.keys()
      .reduce(
        (headers: any, key: any) => {
          headers[key] = (headerValues!.get(key));
          return headers;
        }, {});

    const response: IEntrypointResponse = {
      links: event.body.links,
      sessionHeader,
      initializeParam,
      headers
    };

    this.sessionStorageService.setItem(initializeParam.charonID!, JSON.stringify(response));
  }

  private getEntryPointResponse(charonId: string) {
    const entrypointResponse = JSON.parse(this.sessionStorageService.getItem(charonId)!);
    return entrypointResponse;
  }
}
